import { isEqual } from 'lodash';
import setUserFromResponse from '@/auth/helper';
import {
  assignIfExists,
  assignProps,
  assignValue,
  mapResults,
  removeItemByCode,
  mapItemOrderByValues,
  findMatch,
  isValidFilter,
  findSelectedInCategory,
} from '@/utils/resultsHelper';
import { responseError, translatedError } from '@/utils/errors';
import { findWithUUID } from '@/utils/uuidHelper';
import { states } from '@/constants/lifecycle';
import { makePermissionsForState } from '@/roles/helper';
import { defaultPermissions } from '@/roles/constants';
import withDraft from '@/utils/draftHelper';
import { updateSingularPanels } from '@/utils/panels';
import actions from '../constants';
import {
  userPanels, activeArchiveUsers, usersResult, userStateToLoad, findUsers, findUser, findAllUsers, userArchiveStatus,
} from './state';

const toggleUsersSelected = withDraft((draft, action) => {
  const item = findUsers(draft, action);
  item.results.forEach((result) => {
    assignValue(result, 'selected', action.selected);
  });
});

const updateUsersFilter = withDraft((draft, action) => {
  const item = findUsers(draft, action);
  item.filter = action.value;
});

const updateUsersRoleFilter = withDraft((draft, action) => {
  const item = findUsers(draft, action);
  item.role = action.value;
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.currentPage = 1;
});

const toggleUsersColumnOrderBy = withDraft((draft, { category, orderByColumn }) => {
  const item = findUsers(draft, { category });
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.orderBy = mapItemOrderByValues(item.orderBy, orderByColumn, item.singleSorting);
});

const toggleUsersSingleSorting = withDraft((draft, action) => {
  const item = findUsers(draft, action);
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.singleSorting = !item.singleSorting;
  item.orderBy = [item.orderBy[0]];
});

const fetchUsers = withDraft((draft, action) => {
  const item = findUsers(draft, action);
  if (isValidFilter(action.filter, action)) {
    item.readStatus = states.PENDING;
    item.readErrors = [];
  }
});

const showUsers = withDraft((draft, action) => {
  const item = findUsers(draft, action);
  item.readStatus = states.SUCCESS;
  item.readErrors = [];
  item.results = action.append
    ? mapResults(item.results, action, usersResult) : mapResults([], action, usersResult);
  item.pageSize = action.pageSize;
  item.count = action.resultsCount;
  item.currentPage = action.currentPage;
  item.totalPages = action.totalPages;
});

const showUsersMetrics = withDraft((draft, action) => {
  const item = findUsers(draft, action);
  item.readStatus = states.SUCCESS;
  item.readErrors = [];
  item.results.forEach((result) => {
    const updatedResult = findMatch(action.results, result.uuid, 'uuid');
    if (updatedResult?.metrics && !isEqual(updatedResult.metrics, result.metrics)) {
      assignValue(result, 'metrics', updatedResult.metrics);
    }
  });
});

const showUsersError = withDraft((draft, action) => {
  const item = findUsers(draft, action);
  item.readStatus = states.ERROR;
  item.readErrors = [responseError(action)];
});

const closeUsers = withDraft((draft, action) => {
  draft.users[action.category] = userStateToLoad({ auth: draft }).users[action.category];
});

const updateUsersFlags = withDraft((draft, action) => {
  const item = findUsers(draft, action);
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.currentPage = 1;
  item.approvedFlag = action.approvedFlag;
  item.unapprovedFlag = action.unapprovedFlag;
  item.archivedFlag = action.archivedFlag;
});

const showArchiveUsers = withDraft((draft, action) => {
  const items = findSelectedInCategory(draft.users, action.category);
  draft.archiveUsers.open = !items[0].archived;
  draft.archiveUsers.restoreOpen = items[0].archived;
  draft.users.archival.results = items;
});

const toggleUsersPresentation = withDraft((draft, action) => {
  const item = findUsers(draft, action);
  item.display = action.display;
  item.readStatus = states.PENDING;
  item.readErrors = [];
  item.results = [];
  item.count = 0;
  item.currentPage = 1;
  item.totalPages = 0;
});

const toggleUserSelected = withDraft((draft, action) => {
  const item = findUser(draft, action);
  item.selected = !item.selected;
});

const fetchUser = withDraft((draft, action) => {
  const item = findUser(draft, action);
  if (item) {
    item.readStatus = states.PENDING;
    item.readErrors = [];
  } else {
    draft.users[action.category].results.push({
      code: action.code,
      readStatus: states.PENDING,
      readErrors: [],
    });
  }
});

const showUser = withDraft((draft, action) => {
  const item = findUser(draft, action);
  assignProps(item, {
    ...action.result,
    ...userPanels(),
    readStatus: states.SUCCESS,
    readErrors: [],
  });
});

const showUserError = withDraft((draft, action) => {
  const item = findUser(draft, action);
  item.readStatus = states.ERROR;
  item.readErrors = [responseError(action)];
});

const closeUser = withDraft((draft, action) => {
  const item = findUser(draft, action);
  if (item) {
    draft.users[action.category].results = removeItemByCode(draft.users[action.category].results, item);
  }
});

const updateUserApproved = withDraft((draft, action) => {
  const item = findUser(draft, action);
  item.updateStatus = states.PENDING;
  item.updateErrors = [];
});

const updateUserApprovedSuccess = withDraft((draft, action) => {
  const item = findUser(draft, action);
  item.updateStatus = states.SUCCESS;
  item.approved = action.approved;
  item.updateErrors = [];
});

const updateUserApprovedError = withDraft((draft, action) => {
  const item = findUser(draft, action);
  item.updateStatus = states.ERROR;
  item.updateErrors = [responseError(action)];
});

const updateUserSearchable = withDraft((draft, action) => {
  const item = findUser(draft, action);
  item.updateStatus = states.PENDING;
  item.updateErrors = [];
});

const updateUserSearchableSuccess = withDraft((draft, action) => {
  const item = findUser(draft, action);
  item.updateStatus = states.SUCCESS;
  item.searchable = action.searchable;
  item.updateErrors = [];
});

const updateUserSearchableError = withDraft((draft, action) => {
  const item = findUser(draft, action);
  item.updateStatus = states.ERROR;
  item.updateErrors = [responseError(action)];
});

const showArchiveUser = withDraft((draft, action) => {
  const item = findUser(draft, action);
  draft.archiveUsers.open = !item.archived;
  draft.archiveUsers.restoreOpen = item.archived;
  draft.users.archival.results = [item];
});

const updateUserPanels = withDraft((draft, action) => {
  const item = findUser(draft, action);
  item.panels = updateSingularPanels(item, action);
});

const applyCurrentUser = withDraft((draft, action) => {
  draft.currentUser = setUserFromResponse(action.result);
  draft.currentUser.isGuest = false;
});

const fetchGuestUser = withDraft((draft) => {
  draft.role.isLoading = true;
});

const applyGuestUser = withDraft((draft, action) => {
  draft.currentUser = setUserFromResponse(action.result);
  draft.currentUser.isGuest = true;
});

const applyRole = withDraft((draft, action) => {
  assignIfExists(draft.role, { ...action.role });
  draft.role.isLoading = false;
  draft.role.approved = action.approved;
  draft.role.permissions = makePermissionsForState(action.permissions, action.approved);
});

const applyPermissions = withDraft((draft, action) => {
  draft.role.approved = action.approved;
  draft.role.permissions = action.approved ? action.permissions : defaultPermissions;
});

const applyCommonsMode = withDraft((draft, action) => {
  draft.commonsMode = action.value;
});

const applyUserGroups = withDraft((draft, action) => {
  draft.userGroups = action.userGroups;
});

const roleUpdated = withDraft((draft, action) => {
  if (action.code === draft.role.code) {
    draft.role.name = action.result.name;
    draft.role.description = action.result.description;
    draft.role.permissions = makePermissionsForState(action.result.permissions, draft.role.approved);
  }
});

const roleChanged = withDraft((draft, action) => {
  assignIfExists(draft.role, {
    ...action.result,
    permissions: makePermissionsForState(action.result.permissions, draft.role.approved),
  });
});

const fetchUserError = withDraft((draft, action) => {
  draft.currentUser = { readStatus: states.ERROR, readErrors: [responseError(action)] };
  draft.role.isLoading = false;
  draft.role.approved = false;
  draft.role.permissions = defaultPermissions;
});

const toggleMyApproval = withDraft((draft) => {
  draft.role.approved = !draft.role.approved;
});

const toggleMyArchived = withDraft((draft) => {
  const error = translatedError({}, '', 'user.archived.login.message');
  draft.currentUser = { readStatus: states.ERROR, readErrors: [error] };
  draft.role.approved = false;
  draft.role.permissions = defaultPermissions;
});

const applyRoleActions = withDraft((draft, action) => {
  draft.users.open.roleActions.readStatus = states.SUCCESS;
  draft.users.open.roleActions.results = action.roleActions;
});

const hideArchiveUser = withDraft((draft) => {
  draft.archiveUsers = activeArchiveUsers();
  draft.users.archival.results = [];
});

const archiveUsers = withDraft((draft, action) => {
  const props = userArchiveStatus(action, states.PENDING);
  findAllUsers(draft, action).forEach((category) => {
    action.items.forEach(({ uuid }) => {
      const userInCategory = findWithUUID(draft.users[category].results, uuid);
      if (userInCategory) {
        assignIfExists(userInCategory, { ...props });
      }
    });
  });
});

const archiveUsersSuccess = withDraft((draft, action) => {
  const props = userArchiveStatus(action, states.SUCCESS);
  findAllUsers(draft, action).forEach((category) => {
    action.items.forEach((u) => {
      const userInCategory = findWithUUID(draft.users[category].results, u.uuid);
      if (userInCategory) {
        assignIfExists(userInCategory, { ...u, ...props });
      }
    });
  });

  draft.archiveUsers = activeArchiveUsers();
  draft.users.archival.results = [];
});

const archiveUsersError = withDraft((draft, action) => {
  findAllUsers(draft, action).forEach((category) => {
    action.items.forEach((item) => {
      const userInCategory = findWithUUID(draft.users[category].results, item.uuid);
      if (userInCategory) {
        assignIfExists(userInCategory, userArchiveStatus(item, states.ERROR));
      }
    });
  });
  draft.archiveUsers = activeArchiveUsers();
  draft.users.archival.results = [];
});

export default {
  [actions.APPLY_COMMONS_MODE]: applyCommonsMode,
  [actions.APPLY_CURRENT_USER]: applyCurrentUser,
  [actions.APPLY_GUEST_USER]: applyGuestUser,
  [actions.APPLY_PERMISSIONS]: applyPermissions,
  [actions.APPLY_ROLE]: applyRole,
  [actions.APPLY_ROLE_ACTIONS]: applyRoleActions,
  [actions.APPLY_USER_GROUPS]: applyUserGroups,
  [actions.ARCHIVE_USERS]: archiveUsers,
  [actions.ARCHIVE_USERS_ERROR]: archiveUsersError,
  [actions.ARCHIVE_USERS_SUCCESS]: archiveUsersSuccess,
  [actions.RESTORE_USERS]: archiveUsers,
  [actions.CLOSE_USER]: closeUser,
  [actions.CLOSE_USERS]: closeUsers,
  [actions.FETCH_GUEST_USER]: fetchGuestUser,
  [actions.FETCH_GUEST_USER_ERROR]: fetchUserError,
  [actions.FETCH_USER]: fetchUser,
  [actions.FETCH_USER_ERROR]: fetchUserError,
  [actions.FETCH_USERS]: fetchUsers,
  [actions.HIDE_ARCHIVE_USER]: hideArchiveUser,
  [actions.REFRESH_USERS]: fetchUsers,
  [actions.REFRESH_USERS_METRICS]: fetchUsers,
  [actions.ROLE_CHANGED]: roleChanged,
  [actions.ROLE_UPDATED]: roleUpdated,
  [actions.SHOW_ARCHIVE_USER]: showArchiveUser,
  [actions.SHOW_ARCHIVE_USERS]: showArchiveUsers,
  [actions.SHOW_USER]: showUser,
  [actions.SHOW_USER_ERROR]: showUserError,
  [actions.SHOW_USERS]: showUsers,
  [actions.SHOW_USERS_ERROR]: showUsersError,
  [actions.SHOW_USERS_METRICS]: showUsersMetrics,
  [actions.SHOW_USERS_METRICS_ERROR]: showUsersError,
  [actions.TOGGLE_MY_APPROVAL]: toggleMyApproval,
  [actions.TOGGLE_MY_ARCHIVED]: toggleMyArchived,
  [actions.TOGGLE_USER_SELECTED]: toggleUserSelected,
  [actions.TOGGLE_USERS_COLUMN_ORDER_BY]: toggleUsersColumnOrderBy,
  [actions.TOGGLE_USERS_PRESENTATION]: toggleUsersPresentation,
  [actions.TOGGLE_USERS_SELECTED]: toggleUsersSelected,
  [actions.TOGGLE_USERS_SINGLE_SORTING]: toggleUsersSingleSorting,
  [actions.UPDATE_USER_APPROVED]: updateUserApproved,
  [actions.UPDATE_USER_APPROVED_ERROR]: updateUserApprovedError,
  [actions.UPDATE_USER_APPROVED_SUCCESS]: updateUserApprovedSuccess,
  [actions.UPDATE_USER_PANELS]: updateUserPanels,
  [actions.UPDATE_USER_SEARCHABLE]: updateUserSearchable,
  [actions.UPDATE_USER_SEARCHABLE_ERROR]: updateUserSearchableError,
  [actions.UPDATE_USER_SEARCHABLE_SUCCESS]: updateUserSearchableSuccess,
  [actions.UPDATE_USERS_FILTER]: updateUsersFilter,
  [actions.UPDATE_USERS_FLAGS]: updateUsersFlags,
  [actions.UPDATE_USERS_ROLE_FILTER]: updateUsersRoleFilter,
};
